import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import TaskDrawer from './TaskDrawer';
import StorageFactory from '../../../Util/StorageFactory';
import {
  ACCESS_RESTRICTED_MESSAGE,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  STATUS_COMPLETED,
  STATUS_INCOMPLETED,
  STATUS_INPROGRESS,
  STATUS_PENDING,
  TEAM_MEMBERS_REQUIRED_MESSAGE,
} from '../../../Util/VMConstants';
import {
  API_GET_DELETE_TASK,
  API_GET_TASK_DETAILS,
} from '../../../Util/URLConstants';
import { align_center, align_left, VM_Colors } from '../../../Styles/VMStyles';
import {
  formatDate,
  getPriorityColor,
  taskStatusConfig,
} from '../../../Util/ConstnatUtils';
import MUITypography from '../../../Components/MUITypography';
import StatusButton from '../../../Components/StatusButton';
import ReusableDrawer from '../../../Components/ReusableDrawer';
import TaskHistory from './TaskHistory';
import { checkIsPermissionExist, hasAdminTeamMembers, renderSNo, updateColorBasedOnStatus } from '../../../Util/UtilHelper';
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from '../../../Util/recordAuthorization';
import SubscriptionDialog from '../../../Util/SubscriptionDialog';
import MUIAlertDialog from '../../../Components/MUIAlertDialog';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import TitleWithAddButton from '../../Home/TitleWithAddButton';
import { useSelector } from 'react-redux';
import NoPermission from '../../../Components/NoPermission';
import ReusableDataGrid from '../../../Components/ReusableDataGrid';
import { useNavigate } from 'react-router-dom';


const formatEnquirySummary = (info) => {
  if (info == null || info === '') return null;
  if (typeof info === 'string') return info;
  if (typeof info === 'object') {
    const bits = [
      info.title,
      info.customer_name,
      info.event_type,
      info.event_date,
    ].filter(Boolean);
    if (bits.length) return bits.join(' · ');
    try {
      return JSON.stringify(info);
    } catch {
      return '—';
    }
  }
  return String(info);
};

const DETAIL_LABEL_WIDTH = 160;

const DetailRow = ({ label, children }) => (
  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
    <Box
      sx={{
        width: { xs: '100%', sm: DETAIL_LABEL_WIDTH },
        minWidth: { sm: DETAIL_LABEL_WIDTH },
        maxWidth: { sm: DETAIL_LABEL_WIDTH },
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  </Box>
);

const VMTasksList = () => {
  const [tasks, setTasks] = useState([]);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [taskDetailDrawerOpen, setTaskDetailDrawerOpen] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [reuseTaskData, setReuseTaskData] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const [teamMembersAlertOpen, setTeamMembersAlertOpen] = useState(false);

  // New state for delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const filterStatuses = ['All', STATUS_PENDING, STATUS_INPROGRESS, STATUS_COMPLETED];

  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
    completionRate: 0,
  });
  const subscriptionStatus = useSelector(
    (state) => state.subscription.status
  );
  // const theme = useTheme();
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  const openTaskDrawerIfTeamExists = useCallback(
    async (onOpen) => {
      const hasTeam = await hasAdminTeamMembers(userID);
      if (hasTeam) {
        onOpen();
      } else {
        setTeamMembersAlertOpen(true);
      }
    },
    [userID]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userID,
        page: String(page),
        no_of_rows: String(pageSize),
        search: searchTerm,
        status: statusFilter === 'All' ? '' : statusFilter,
        priority: priorityFilter === 'All' ? '' : priorityFilter,
        from_date: fromDate,
        to_date: toDate,
      });

      const url = `${API_GET_TASK_DETAILS}?${params.toString()}`;
      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        setTasks(resp.data);
        setTotalCount(resp.totalCount ?? resp.data.length ?? 0);
        const summary = resp?.summary || {};
        setDashboardStats({
          total: summary.total_tasks ?? 0,
          completed: summary.completed_tasks ?? 0,
          inProgress: summary.in_progress_tasks ?? 0,
          pending: summary.pending_tasks ?? 0,
          completionRate:
            summary.total_tasks > 0
              ? Math.round((summary.completed_tasks / summary.total_tasks) * 100)
              : 0,
        });
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setTasks([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      toast.error('Failed to load tasks');
      setTasks([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fromDate, page, pageSize, priorityFilter, searchTerm, statusFilter, toDate, userID]);

  useEffect(() => {
    fetchData();
  }, [fetchData, isDrawerOpen]);

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const deleteTask = async (task_id) => {
    const record = tasks.find((t) => String(t.id) === String(task_id));
    if (record && !canMutateRecord(VM_PERMISSIONS, 'VM030', record)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }
    try {
      const url = `${API_GET_DELETE_TASK}?user_id=${userID}&id=${task_id}`;
      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200) {
        toast.success('Task deleted successfully');
        fetchData();
      } else {
        toast.error(resp.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error deleting task');
    }
  };

  // Open confirmation dialog
  const handleOpenDeleteDialog = (taskId) => {
    setDeleteTaskId(taskId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteTaskId(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (deleteTaskId) {
      await deleteTask(deleteTaskId);
    }
    handleCloseDeleteDialog();
  };

  const openTaskDetailDrawer = (row) => {
    setSelectedTaskDetail(row);
    setTaskDetailDrawerOpen(true);
  };

  const closeTaskDetailDrawer = () => {
    setTaskDetailDrawerOpen(false);
    setSelectedTaskDetail(null);
  };

  const filteredTasks = useMemo(() => {
    return tasks.map((task, idx) => ({
      id: task.id,
      s_no: idx + 1 + page * pageSize,
      name: task.name,
      description: task.description,
      status: task.task_status,
      priority: task.priority,
      due_date: task.due_date,
      assigned_users: task.assign_users,
      enquiry_info: task.enquiry_info,
      created_by: task.created_by,
    }));
  }, [page, pageSize, tasks]);

  const columns = [
        renderSNo(),
    {
      field: 'name',
      headerName: 'Task Name',
      flex: 1.5,
      renderCell: (params) => (
        <Tooltip title={params.row.name || ''}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              ...align_left,
            }}
          >
            <MUITypography variant="body" noWrap sx={{ width: '100%', minWidth: 0 }}>
              {params.value || '-'}
            </MUITypography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'assigned_users',
      headerName: 'Assigned To',
      flex: 1,
      // width: 180,
      // minWidth: 180,
      // maxWidth: 180,
      renderCell: (params) => {
        const users = Array.isArray(params.value) ? params.value : [];
        if (!users.length) return '—';
        const tooltipTitle = users
          .map((user) => `${user.name || ''}${user.email ? ` · ${user.email}` : ''}`)
          .join('\n');
        const [first, ...rest] = users;
        return (
          <Tooltip title={tooltipTitle}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                flexWrap: 'nowrap',
              }}
            >
              <Chip
                label={first.name}
                size="small"
                sx={{
                  maxWidth: 140,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
              {rest.length > 0 && (
                <Chip label={`+${rest.length}`} size="small" variant="outlined" />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.3,
      renderCell: (params) => (
        <Box sx={{ width: '100%',height:'100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <StatusButton status={params.value} />
        </Box>
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      // width: 180,
      // minWidth: 180,
      // maxWidth: 180,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%',height:'100%', justifyContent: 'flex-start' }}>
          {/* <CalendarToday fontSize="small" /> */}
          <span>{formatDate(params.value)}</span>
        </Stack>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 120,
      // flex: 0.6,
      renderCell: (params) => (
        <Box sx={{ width: '100%',height:'100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Chip
            label={`${params.value}`}
            color={getPriorityColor(params.value)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Details',
      minWidth: 70,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ width: '100%',height:'100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
            onClick={() => openTaskDetailDrawer(params.row)}
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  columns.push({
  field: 'actions',
  headerName: 'Actions',
  // width: 100,
  renderCell: (params) => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Stack direction="row" alignItems="center">
        {/* 🔁 REUSE TASK BUTTON */}
        <Tooltip title="Reuse Task">
          <IconButton
            color="primary"
            onClick={() => {
              openTaskDrawerIfTeamExists(() => {
                setReuseTaskData(params.row);
                setIsDrawerOpen(true);
              });
            }}
          >
            <ReplayCircleFilledIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* ❌ DELETE TASK BUTTON (existing code remains) */}
        {canMutateRecord(VM_PERMISSIONS, 'VM030', params.row) && (
          <IconButton color="error" onClick={() => handleOpenDeleteDialog(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
    </Box>
  ),
}
  );
  

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <TextField
        size="small"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ mr: 2, width: 200 }}
        InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1 }} /> }}
      />
      <FormControl size="small" sx={{ mr: 2, width: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value={STATUS_COMPLETED}>{STATUS_COMPLETED}</MenuItem>
          <MenuItem value={STATUS_INPROGRESS}>{STATUS_INPROGRESS}</MenuItem>
          <MenuItem value={STATUS_PENDING}>{STATUS_PENDING}</MenuItem>
          <MenuItem value={STATUS_INCOMPLETED}>{STATUS_INCOMPLETED}</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ mr: 2, width: 150 }}>
        <InputLabel>Priority</InputLabel>
        <Select
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="High">High</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Low">Low</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        type="date"
        label="From"
        InputLabelProps={{ shrink: true }}
        value={fromDate}
        onChange={(e) => {
          setFromDate(e.target.value);
          setPage(0);
        }}
        sx={{ mr: 2 }}
      />
      <TextField
        size="small"
        type="date"
        label="To"
        InputLabelProps={{ shrink: true }}
        value={toDate}
        onChange={(e) => {
          setToDate(e.target.value);
          setPage(0);
        }}
        sx={{ mr: 2 }}
      />
      <Button
        variant="outlined"
        onClick={() => {
          setSearchTerm('');
          setStatusFilter('All');
          setPriorityFilter('All');
          setFromDate('');
          setToDate('');
          setPage(0);
        }}
      >
        Reset
      </Button>
    </GridToolbarContainer>
  );

  return (
    <Box >
      {/* Header + Create Task */}
  <TitleWithAddButton
        title="Tasks"
        buttonLabel="Create Task"
        onAddClick={() => {
               if (subscriptionStatus?.plan === 'premium' || subscriptionStatus?.plan === 'trial') {
                  openTaskDrawerIfTeamExists(() => {
                    setReuseTaskData(null);
                    setIsDrawerOpen(true);
                    setOpenSubscribeDialog(false);
                  });
                } else {
                  setOpenSubscribeDialog(true);
                }
        } }
        isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS, 'VM028')}
      />


      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Tasks
          </Typography>
          <Typography variant="h5" color="text.secondary">
            Streamline your workflow with intelligent task management
          </Typography>
        </Box>
        {checkIsPermissionExist(VM_PERMISSIONS, 'VM028') && (
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => 
            {
               if (isSubscribed) {

                  setIsDrawerOpen(true);
                  setOpenSubscribeDialog(false);
                } else {
                  // open subscription dialog
                  setOpenSubscribeDialog(true);
                }
            }
            }
          >
            Create Task
          </Button>
        )}
      </Box> */}

      {/* Dashboard Stats */}
      <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',                 // mobile
          sm: 'repeat(2, 1fr)',      // tablet
          md: 'repeat(4, 1fr)',      // desktop
        },
        gap: 2,
      }}
    >
{taskStatusConfig.map((status, index) => (
  <Card
    key={index}
    elevation={2}
    sx={{
      height: '100%',
      borderRadius: 2,
      borderLeft: `4px solid`,
      borderColor: status.borderColor,
    }}
  >
    <CardContent
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      {/* LEFT CONTENT */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {status.title}
        </Typography>

        <Typography
          variant="h4"
          fontWeight="bold"
          color={status.textColor}
          sx={{ mt: 0.5 }}
        >
          {dashboardStats?.[status.statKey] ?? 0}
        </Typography>

        {/* {status.subtitle && (
          <Typography variant="caption" color="text.secondary">
            {status.subtitle}
          </Typography>
        )} */}
      </Box>

      {/* RIGHT ICON */}
      <Box
        sx={{
          width: { xs: 36, md: 40 },
          height: { xs: 36, md: 40 },
          borderRadius: 2,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette[status.gradientFrom?.split('.')[0]]?.light || status.gradientFrom}, transparent)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={status.icon}
          alt={status.title}
          sx={{
            width: 22,
            height: 22,
            objectFit: 'contain',
          }}
        />
      </Box>
    </CardContent>
  </Card>
))}

    </Box>

      {/* Status filter chips + count */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {filterStatuses.map((status) => (
            <Chip
              key={status}
              label={status}
              color={status === statusFilter ? 'primary' : 'default'}
              onClick={() => handleFilterChange(status)}
              clickable
              sx={{
                backgroundColor:updateColorBasedOnStatus(status),
                  // status === statusFilter ? VM_Colors.secondary : VM_Colors.primary,
                color: VM_Colors.white,
                fontWeight: status === statusFilter ? 'bold' : 'normal',
                border: status === statusFilter ? '2px solid white' : 'none',
                '&:hover': {
                  backgroundColor:updateColorBasedOnStatus(status),
                    // status === statusFilter ? VM_Colors.secondary : VM_Colors.primary,
                },
              }}
            />
          ))}
        </Box>

        <Box sx={{ ml: 'auto', paddingRight: '10px' }}>
          <MUITypography>Count: {totalCount}</MUITypography>
        </Box>
      </Box>

      {/* DataGrid */}
      {checkIsPermissionExist(VM_PERMISSIONS, 'VM029') ? (
        <Box mt={3} sx={{ width: '100%', backgroundColor: VM_Colors.white }}>
          <ReusableDataGrid
            rows={filteredTasks}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[5, 10, 20, 50]}
            paginationMode="server"
            autoHeight
            disableColumnSorting
            slots={{ toolbar: CustomToolbar }}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooterSelectedRowCount
            customSx={{
              backgroundColor: VM_Colors.white,
              '& .MuiDataGrid-cell': {
                justifyContent: 'flex-start !important',
                alignItems: 'center !important',
                textAlign: 'left !important',
              },
              '& .MuiDataGrid-cell > *': {
                textAlign: 'left',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'flex-start !important',
              },
            }}
          />
        </Box>
      )
      :  <Box sx={{ mt: 4 }}>
            <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />  
        </Box>
    }

      <ReusableDrawer
        open={taskDetailDrawerOpen}
        title="Task Details"
        onClose={closeTaskDetailDrawer}
        showFooter={false}
      >
        <Box sx={{ p: 2 }}>
          {selectedTaskDetail ? (
            <>
              <Box sx={{ position: 'relative', pr: 14, mb: 2 }}>
                <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                  <StatusButton status={selectedTaskDetail.status} />
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Task #{selectedTaskDetail.id}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: VM_Colors.primary }}>
                  {selectedTaskDetail.name || 'Untitled task'}
                </Typography>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Overview
                  </Typography>
                  <Chip
                    label={selectedTaskDetail.priority || '—'}
                    color={getPriorityColor(selectedTaskDetail.priority)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <DetailRow label="Due date" >
                  <Typography variant="body2">{formatDate(selectedTaskDetail.due_date) || '—'}</Typography>
                </DetailRow>
                <DetailRow label="Assigned to">
                  {Array.isArray(selectedTaskDetail.assigned_users) &&
                  selectedTaskDetail.assigned_users.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {selectedTaskDetail.assigned_users.map((u, idx) => (
                        <Chip key={`${u.email || idx}`} label={u.name || u.email || 'User'} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </DetailRow>
                <DetailRow label="Created by">
                  {selectedTaskDetail.created_by ? (
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedTaskDetail.created_by.name || '—'}
                      </Typography>
                      {selectedTaskDetail.created_by.email && (
                        <Typography variant="caption" color="text.secondary">
                          {selectedTaskDetail.created_by.email}
                        </Typography>
                      )}
                      {(selectedTaskDetail.created_by.mobile_no ||
                        selectedTaskDetail.created_by.country_code) && (
                        <Typography variant="caption" color="text.secondary">
                          {selectedTaskDetail.created_by.country_code != null &&
                          selectedTaskDetail.created_by.country_code !== ''
                            ? `+${selectedTaskDetail.created_by.country_code} `
                            : ''}
                          {selectedTaskDetail.created_by.mobile_no || ''}
                        </Typography>
                      )}
                      {selectedTaskDetail.created_by.user_type && (
                        <Typography variant="caption" color="text.secondary">
                          Role: {selectedTaskDetail.created_by.user_type}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </DetailRow>
                {/* {formatEnquirySummary(selectedTaskDetail.enquiry_info) && (
                  <DetailRow label="Linked enquiry">
                    <Typography variant="body2">
                      {formatEnquirySummary(selectedTaskDetail.enquiry_info)}
                    </Typography>
                  </DetailRow>
                )} 
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {selectedTaskDetail.description?.trim?.() ? selectedTaskDetail.description : 'No description provided.'}
                </Typography>
                */}
              </Paper>

              <MUITypography variant="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                 History
              </MUITypography>
              {/* <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Updates, comments, and attachments for this task.
              </Typography> */}
              <TaskHistory id={selectedTaskDetail.id} />
            </>
          ) : null}
        </Box>
      </ReusableDrawer>


      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />


      <TaskDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setReuseTaskData(null);
        }}
        onCreateTask={fetchData}
        reuseTaskData={reuseTaskData}
        
      />

      <MUIAlertDialog
        open={teamMembersAlertOpen}
        onClose={() => setTeamMembersAlertOpen(false)}
        title="Team members required"
        message={TEAM_MEMBERS_REQUIRED_MESSAGE}
        okClickHandler={() => {setTeamMembersAlertOpen(false) 
            navigate('/team');
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this task?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VMTasksList;
